import type { UpgradeDef } from '../../types';

export const SKILL_NAME = 'farming' as const;

export const FARMING_UPGRADES: UpgradeDef[] = [
    // =========================================================================
    // FARMING BRANCH
    // =========================================================================

    // --- TIER 1 ---
    {
        id: 'farming_gloves',
        label: 'Farming Gloves',
        icon: 'Hand',
        position: { x: 100, y: 600 },
        description: 'Better grip for working the land. +5% Farming Speed.',
        cost: { gold: 25 },
        effects: [{ type: 'farming_speed', value: 0.95 }]
    },
    {
        id: 'farming_sickle',
        label: 'Rusty Sickle',
        icon: 'Scissors',
        position: { x: 100, y: 400 },
        description: 'Basic harvesting tool. +10% Farming Speed.',
        cost: { gold: 50 },
        effects: [{ type: 'farming_speed', value: 0.9 }]
    },
    {
        id: 'seed_pouch',
        label: 'Seed Pouch',
        icon: 'Package',
        position: { x: 100, y: 700 }, // Down
        description: 'Organized seeds are cheaper to buy. -10% Seed Cost.',
        cost: { gold: 50 },
        effects: [{ type: 'seed_cost_reduction', value: 0.1 }]
    },
    {
        id: 'farming_basic_seeds',
        label: 'Premium Seeds',
        icon: 'Sprout',
        position: { x: 100, y: 500 },
        description: 'Better genetic stock. +50% XP from all crops.',
        cost: { gold: 150 },
        effects: [{ type: 'multiply_xp', target: 'farming', value: 1.5 }]
    },

    // --- TIER 1.5 / 2 ---
    {
        id: 'compost_bin',
        label: 'Compost Bin',
        icon: 'Trash2',
        position: { x: 200, y: 450 },
        description: 'Recycle waste into value. +2 Gold per harvest.',
        cost: { gold: 100 },
        prerequisites: ['farming_sickle'],
        effects: [{ type: 'gold_on_harvest', value: 2 }]
    },
    {
        id: 'farming_irrigation',
        label: 'Irrigation',
        icon: 'Droplets',
        position: { x: 300, y: 450 },
        description: 'Crops grow 25% faster.',
        cost: { gold: 300 },
        prerequisites: ['farming_basic_seeds'],
        effects: [{ type: 'farming_speed', value: 0.75 }]
    },
    {
        id: 'farming_fertilizer',
        label: 'Chemical Fertilizer',
        icon: 'FlaskConical',
        position: { x: 500, y: 350 },
        description: 'Crops grow another 25% faster.',
        cost: { gold: 1200 },
        prerequisites: ['farming_irrigation'],
        effects: [{ type: 'farming_speed', value: 0.75 }]
    },

    // --- CROP MASTERY 1 ---
    {
        id: 'potato_mastery',
        label: 'Potato Genetics',
        icon: 'Circle', // Representing a potato
        position: { x: 300, y: 350 },
        description: 'Master the Potato. +50% Yield.',
        cost: { gold: 200 },
        prerequisites: ['farming_sickle'],
        effects: [{ type: 'multiply_yield', target: 'potato', value: 1.5 }]
    },
    {
        id: 'farming_corn_mastery',
        label: 'Corn Mastery',
        icon: 'Sun', // Yellow and spiky?
        position: { x: 300, y: 550 },
        description: 'Unlock the secrets of Corn. Double Corn yield.',
        cost: { gold: 500 },
        prerequisites: ['farming_basic_seeds'],
        effects: [{ type: 'multiply_yield', target: 'corn', value: 2 }]
    },
    {
        id: 'wheat_mastery',
        label: 'Wheat Thresher',
        icon: 'Wheat',
        position: { x: 300, y: 250 },
        description: 'Efficient grain processing. +50% Wheat Yield.',
        cost: { gold: 400 },
        prerequisites: ['farming_sickle'],
        effects: [{ type: 'multiply_yield', target: 'wheat', value: 1.5 }]
    },

    // --- NEW MECHANICS ---
    {
        id: 'gnome_hut_basic',
        label: 'Gnome Hut',
        icon: 'Tent', // Represents a hut
        position: { x: 300, y: 650 },
        description: 'A helper to tend your crops. Unlocks 1 Assignment Slot.',
        cost: { gold: 1000 },
        prerequisites: ['seed_pouch'],
        effects: [{ type: 'unlock_mechanic', target: 'gnome_huts', value: 1 }]
    },
    {
        id: 'rain_totem',
        label: 'Rain Totem',
        icon: 'CloudRain',
        position: { x: 400, y: 600 },
        description: 'Active Ability: Advance all crops by 15 minutes. (10m Cooldown)',
        cost: { gold: 2500, stone: 100 },
        prerequisites: ['seed_pouch'],
        effects: [{ type: 'unlock_mechanic', target: 'rain_totem' }]
    },
    {
        id: 'genetic_lab',
        label: 'Genetic Lab',
        icon: 'FlaskConical',
        position: { x: 400, y: 700 },
        description: 'Unlock "Mutate" action on crops. Risk destruction for rewards.',
        cost: { gold: 5000, herbs: 50 },
        prerequisites: ['gnome_hut_basic'],
        effects: [{ type: 'unlock_mechanic', target: 'genetic_mutator' }]
    },

    // --- TIER 3 ---
    {
        id: 'sprinkler_system',
        label: 'Sprinklers',
        icon: 'CloudRain',
        position: { x: 500, y: 550 },
        description: 'Automated watering. +15% Speed.',
        cost: { gold: 1500, copper_bar: 10 },
        prerequisites: ['farming_irrigation'],
        effects: [{ type: 'farming_speed', value: 0.85 }]
    },
    {
        id: 'farming_carrot_gold',
        label: 'Golden Carrots',
        icon: 'Carrot',
        position: { x: 500, y: 450 },
        description: 'Carrots yield 2x more.',
        cost: { gold: 1000 },
        prerequisites: ['farming_corn_mastery'],
        effects: [{ type: 'multiply_yield', target: 'carrot', value: 2 }]
    },
    {
        id: 'hops_mastery',
        label: 'Hops Filter',
        icon: 'Beer',
        position: { x: 500, y: 250 },
        description: 'Brewing perfection. +50% Hops Yield.',
        cost: { gold: 1500, wood: 50 },
        prerequisites: ['wheat_mastery'],
        effects: [{ type: 'multiply_yield', target: 'hops', value: 1.5 }]
    },

    // --- TIER 4 ---
    {
        id: 'farming_scythe',
        label: 'Golden Scythe',
        icon: 'Scissors',
        position: { x: 700, y: 350 },
        description: '+10% Yield from all crops.',
        cost: { gold: 2000, wood: 100 },
        prerequisites: ['farming_fertilizer'],
        effects: [{ type: 'multiply_yield', target: 'all', value: 1.1 }]
    },
    {
        id: 'hydroponics',
        label: 'Hydroponics',
        icon: 'Pipette',
        position: { x: 700, y: 550 },
        description: 'Soil-less farming. +20% Speed.',
        cost: { gold: 3000, iron_bar: 20 },
        prerequisites: ['sprinkler_system'],
        effects: [{ type: 'farming_speed', value: 0.8 }]
    },
    {
        id: 'farming_gmo',
        label: 'G.M.O.',
        icon: 'Dna',
        position: { x: 700, y: 450 },
        description: 'Genetically Modified Organisms. DOUBLE yield for ALL crops.',
        cost: { gold: 5000 },
        prerequisites: ['farming_scythe', 'farming_carrot_gold'],
        effects: [{ type: 'multiply_yield', target: 'all', value: 2 }]
    },
    {
        id: 'gnome_hut_advanced',
        label: 'Gnome Village',
        icon: 'Tent', // Or 'Castle' / 'Home'
        position: { x: 600, y: 600 },
        description: 'More helpers. Unlocks 2 more Assignment Slots.',
        cost: { gold: 10000 },
        prerequisites: ['rain_totem'],
        effects: [{ type: 'unlock_mechanic', target: 'gnome_huts', value: 2 }]
    },
    {
        id: 'herbalism',
        label: 'Herbalism',
        icon: 'Sprout',
        position: { x: 600, y: 250 },
        description: 'Medicinal knowledge. +30% XP from Herbs.',
        cost: { gold: 2000 },
        prerequisites: ['hops_mastery'],
        effects: [{ type: 'multiply_xp', target: 'herbs', value: 1.3 }]
    },

    // --- ENDGAME / SPECIAL ---
    {
        id: 'farming_tractor',
        label: 'Auto-Tractor',
        icon: 'Truck',
        position: { x: 900, y: 350 },
        description: 'Industrial farming. +20% Speed.',
        cost: { gold: 10000, iron_bar: 100 },
        prerequisites: ['farming_gmo'],
        effects: [{ type: 'farming_speed', value: 0.8 }]
    },
    {
        id: 'farming_greenhouse',
        label: 'Greenhouse',
        icon: 'Warehouse',
        position: { x: 900, y: 400 },
        description: 'Crops grow instantly? (Speed x0.1).',
        cost: { gold: 20000, stone: 500 }, // Replaced Glass with Stone
        prerequisites: ['farming_gmo'],
        effects: [{ type: 'farming_speed', value: 0.1 }]
    },
    {
        id: 'botanical_research',
        label: 'Botanical Research',
        icon: 'BookOpen',
        position: { x: 800, y: 650 },
        description: 'Advanced studies. +20% XP (All).',
        cost: { gold: 5000, mithril_bar: 5 },
        prerequisites: ['genetic_lab'],
        effects: [{ type: 'multiply_xp', target: 'farming', value: 1.2 }]
    },
    {
        id: 'crystal_mastery',
        label: 'Crystal Resonance',
        icon: 'Gem',
        position: { x: 900, y: 250 },
        description: 'Sing to the trees. +100% Crystal Tree Yield.',
        cost: { gold: 50000, time_crystals: 1 },
        prerequisites: ['herbalism'],
        effects: [{ type: 'multiply_yield', target: 'crystal_tree', value: 2 }]
    },
    {
        id: 'miracle_grow',
        label: 'Miracle Grow',
        icon: 'Sparkles',
        position: { x: 800, y: 500 },
        description: '5% Chance for INSTANT growth upon planting.',
        cost: { gold: 8000, herbs: 50 }, // Replaced Alchemy Components with Herbs
        prerequisites: ['hydroponics'],
        effects: [{ type: 'instant_grow_chance', value: 0.05 }]
    },
    {
        id: 'golden_trowel',
        label: 'Golden Trowel',
        icon: 'Shovel',
        position: { x: 900, y: 550 },
        description: '5% Critical Harvest Chance (5x Yield).',
        cost: { gold: 15000, gold_bar: 50 },
        prerequisites: ['farming_gmo'],
        effects: [{ type: 'crit_harvest_chance', value: 0.05 }]
    },

    // --- LAND EXPANSION ---
    {
        id: 'land_expansion_1',
        label: 'Land Expansion I',
        icon: 'Map',
        position: { x: 1100, y: 400 },
        description: 'Clear more land. Unlock 1 Farming Plot.',
        cost: { gold: 10000, wood: 500 },
        prerequisites: ['farming_tractor'],
        effects: [{ type: 'unlock_mechanic', target: 'extra_farming_plot', value: 1 }]
    },
    {
        id: 'land_expansion_2',
        label: 'Land Expansion II',
        icon: 'MapPin',
        position: { x: 1100, y: 500 },
        description: 'Clear even more land. Unlock 1 Farming Plot.',
        cost: { gold: 50000, wood: 2000 },
        prerequisites: ['land_expansion_1'],
        effects: [{ type: 'unlock_mechanic', target: 'extra_farming_plot', value: 1 }]
    }
];
